import { type Dispatch, memo, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { type ProductSearchLocationItem } from '../../../shared/types';
import { type SearchReducerAction, SearchReducerActionType } from '../../reducers/searchReducer';
import { DistanceBoundaryInKm } from '../../types/enums/distanceBoundaryInKm';
import { LocationFilterComponent } from '../Location';
import {
    trackDistanceClicked,
    trackDistanceResetClicked,
    trackLocationTypeInClicked,
} from './Filter.tracking';

const messages = defineMessages({
    headline: {
        defaultMessage: 'Lass dich inspirieren',
    },
});

type FilterProps = {
    readonly locationName?: string;
    readonly distanceValue?: number;
    readonly dispatchSearch: Dispatch<SearchReducerAction>;
};

const Filter = memo(({ dispatchSearch, distanceValue, locationName }: FilterProps): JSX.Element => {
    const intl = useIntl();
    const [storedValue, setStoredValue] = useState(distanceValue);
    const [debouncedValue, setDebouncedValue] = useState(distanceValue);
    const [trackOnSubmit, setTrackOnSubmit] = useState(false);

    const onFilterChange = (
        type: SearchReducerActionType,
        value?: ProductSearchLocationItem,
    ): void => {
        dispatchSearch({ type, value });
        setStoredValue(value?.distance);
    };

    const onFilterReset = async (): Promise<void> => {
        onFilterChange(SearchReducerActionType.Distance, {
            distance: DistanceBoundaryInKm.DEFAULT,
        });

        await trackDistanceResetClicked('ResetRadius', 'Location,distance');
    };

    const onFilterSubmit = (sliderValue?: number): void => {
        const distance = sliderValue || storedValue;
        onFilterChange(SearchReducerActionType.Distance, { distance });
        setTrackOnSubmit(true);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedValue(storedValue);
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [storedValue]);

    useEffect(() => {
        if (trackOnSubmit && storedValue === debouncedValue) {
            setTrackOnSubmit(false);

            (async () => {
                await trackLocationTypeInClicked(
                    `${locationName as string}_${String(storedValue)}km`,
                    locationName as string,
                    'Location,distance',
                );
                await trackDistanceClicked(`${String(storedValue)}km`, 'Location,distance');
            })();
        }
    }, [debouncedValue, locationName, storedValue, trackOnSubmit]);

    return (
        <>
            <h1 className="mt-3x mb-1-5x mb-sm-3x theme-text-headline">
                {intl.formatMessage(messages.headline)}
            </h1>
            <LocationFilterComponent
                locationName={locationName}
                onSubmit={onFilterChange}
                storedValue={storedValue}
                onReset={onFilterReset}
                onFilterSubmit={onFilterSubmit}
            />
        </>
    );
});

Filter.displayName = 'GiftBoxFilter';

export { Filter };
